import type { BaseDeviceRawData, ProjectorEidosBlock, ProjectorPowerState } from "./Device"
import { DeviceBrowser, type DeviceBrowserRawData } from "./DeviceBrowser"
import { DeviceGroup, type DeviceGroupRawData } from "./DeviceGroup"
import { DeviceRPi, type DeviceRPiRawData } from "./DeviceRPi"

export type Status =
	| "OFF"
	| "NEW"
	| "UPDATING"
	| "INIT"
	| "IDLE"
	| "PROJECT"
	| "PROJ_TT"
	| "PNC"
	| "LOST"
	| "DORMANT"
	| "NEW"
	| "EMPTY"
	| "OFFLINE"
	| "ONLINE"
	| "PARTIAL"
	| "INACTIVE"
export type DeviceTypeName =
	| "dev_caxedo"
	| "dev_luxedo"
	| "dev_synth"
	| "dev_luxcast"
	| "dev_amalgam"
	| "dev_group"

export interface FirmwarePreferences {
	audio_delay?: number
	auto_sync?: number
	disp_wifi_enabled?: number
	ignored_updates?: Array<string>
}

// These are pulled directly from each @endpoint method within the firmware
export const API_THREAD_DEVICE_ROUTES = [
	"hdmi_boost_apply",
	"hdmi_boost_remove",
	"config_update",
	"display_set_resolution",
	"load_timetable",
	"project_and_capture_v2",
	"launch_auto_exposure",
	"cancel_pnc",
	"download_show",
	"play_scene",
	"master_play_scene",
	"set_image_b64",
	"play_default",
	"activate_blending_preview",
	"group_configuration_updated",
	"stop_blending_preview",
	"projector_power_on",
	"projector_power_off",
	"run_rs232_command",
	"reboot",
	"restart",
	"ssh_tunnel_close",
	"ssh_tunnel_open",
	"update_trigger",
	"network_test",
	"check_network_requirements",
] as const

export type ApiThreadDeviceRoute = (typeof API_THREAD_DEVICE_ROUTES)[number]

export type APIThreadStatus = {
	[key in ApiThreadDeviceRoute]?: {
		id: number
		args: any[]
		status: "NOT_STARTED" | "RUNNING" | "SUCCESS" | "ERROR"
		result: any
		error: string | null
		ts_end: number | null
	}
}

export interface BaseEidosType {
	api_threads?: APIThreadStatus
	status: string
	cache?: {
		shows: {
			[id: number]: {
				id: number
				fsize: number
				mtime: number
				ts_opened: number
			}
		}
		currently_downloading: number | null
		download_queue: number[]
		free_space_mb: number
	}
	config?: {
		fw_config: FirmwarePreferences
	}
	player?: {
		type: "lightshow" | "scene" | "image" | "image_list"
		tstart: number
		id?: number | string
		loops?: number
		times_looped?: number
		index?: number
	}
	display_config?: Array<number>
	display_mode?: string
	message_queue?: Array<string>
	playback_type: "EMPTY" | "SCENE" | "LIGHTSHOW" | "PNC" | "GROUP_PREVIEW" | "UNKNOWN" | string
	pnc?: {
		capture_id: number | "auto_cal"
		imgs_taken: number
		img_total: number
	}
	proj_id?: number
	proj_play_starttime?: number
	projector?: ProjectorEidosBlock
	projector_power?: {
		state: ProjectorPowerState // undefined is highly unlikely, so just treat it as OFF
		controller: "OptomaRS232Controller" | "EpsonRS232Controller" | "InfraredController"
	}
	network?: {
		ssid: string | null
		ip: string | null
		signal_strength: number | null
	}
}

export type AnyDevice = DeviceRPi | DeviceGroup | DeviceBrowser
export type DeviceRawData = DeviceRPiRawData | DeviceGroupRawData | DeviceBrowserRawData

export type EidosTypeOf<T extends BaseDeviceRawData> = T["eidos"] extends BaseEidosType
	? T["eidos"]
	: never
export type DeviceStatusAppearance = {
	color: string
	text: string
}

type PNC_STATE_DEFAULT = {
	state: "AUTO_EXPOSURE" | "LOST" | "SEARCHING" | "WAITING" | "UNKNOWN_ERROR"
}

type PNC_STATE_CAPTURING = {
	state: "CAPTURING"
	captured: number
	total: number
	autoexp_warning: boolean
}

type PNC_STATE_DONE = {
	state: "DONE"
	exit_code: "SUCCESS" | "FAILURE" | "FAILURE_FW" | "CANCELLED" | "TIMED_OUT"
}

export type ProjectAndCaptureState = PNC_STATE_DEFAULT | PNC_STATE_CAPTURING | PNC_STATE_DONE
export type ProjectAndCaptureError = {
	pnc: {
		capture_id: string
		info: {
			missing_camera: boolean
		}
	}
}

export type ImageCaptureConfig = {
	brightness: number
	bucket_size: number
	conf_selected_lighting?: number
	conf_selected_lumens?: number
	exposure_range?: number
	max_exposure?: number
	stdev_mode?: boolean
	num_runs: number
}

export type AdvancedCalibrationOptions = {
	imagegen_inputs: ImageCaptureConfig
	clean_cap: boolean
	take_snapshot: boolean
	exposure_options: {
		exposure_values: number[]
		minimum_brightness: number
		maximum_brightness: number
		prioritize_stdev: boolean
		ideal_metric: number
		clear_frames: number
	}
}
